import {
  COMPANY_SUPPLIER_KIND_LABELS,
  type CompanyCatalogResponse,
  type CompanySupplier,
  SUPPLY_TYPE_LABELS,
  companyCatalogResponseSchema,
  invitationResponseSchema,
} from '@sivan/shared';
import { useId, useState } from 'react';
import { ApiError, NetworkError, apiGet, apiPost, apiPut } from '../api/client';
import { LoadingBlock, Problem } from '../handover/common';
import { Icon } from '../ui/Icon';
import { useLoad } from './use-master-data';

// ספקים וקבלנים (DR-196): the company's contractors, material suppliers and trades, kept once
// for every project; adding a contractor to a project; the contractor's people and the projects
// where they use the supplier portal. The business Super Admin manages it.

type Notice = { kind: 'error' | 'success'; text: string } | null;

const explain = (error: unknown): string => {
  if (error instanceof NetworkError) return 'אין חיבור לשרת. השינוי לא נשמר.';
  if (error instanceof ApiError) {
    const fields = Object.values((error.body['fields'] ?? {}) as Record<string, string>);
    if (fields.includes('duplicate')) return 'השם או הכתובת כבר קיימים.';
    const conflict = (error.body['conflict'] as { code?: string } | undefined)?.code;
    if (conflict === 'stale') return 'הנתונים השתנו בינתיים. רעננו את הדף.';
    if (error.status === 403) return 'אין לך הרשאה לפעולה זו.';
    if (error.status === 404) return 'יצירת קישור הזמנה זמינה רק בסביבה המתארחת.';
  }
  return 'הפעולה נכשלה.';
};

export function SupplierCatalog() {
  const [version, setVersion] = useState(0);
  const [loaded, reload] = useLoad(`company-suppliers:${String(version)}`, () =>
    apiGet('/company-suppliers', companyCatalogResponseSchema),
  );
  const [data, setData] = useState<CompanyCatalogResponse | null>(null);
  const [notice, setNotice] = useState<Notice>(null);
  if (loaded.state === 'loading' && data === null) return <LoadingBlock />;
  if (loaded.state === 'forbidden')
    return <Problem text="רק מנהל־על עסקי מנהל את הספקים והקבלנים." />;
  if (loaded.state === 'error') return <Problem text="טעינת הספקים נכשלה." onRetry={reload} />;
  const catalog = data ?? (loaded.state === 'ready' ? loaded.data : null);
  if (catalog === null) return <LoadingBlock />;
  const save = (promise: Promise<CompanyCatalogResponse>, done: string) =>
    promise.then(
      (next) => {
        setData(next);
        setNotice({ kind: 'success', text: done });
      },
      (error: unknown) => {
        setNotice({ kind: 'error', text: explain(error) });
        setData(null);
        setVersion((v) => v + 1);
      },
    );
  const contractors = catalog.suppliers.filter((s) => s.kind === 'contractor');
  const materials = catalog.suppliers.filter((s) => s.kind === 'supplier');
  return (
    <section className="supplier-catalog" aria-labelledby="supplier-catalog-title">
      <h2 id="supplier-catalog-title" className="section-title">
        ספקים וקבלנים
      </h2>
      <p className="hint">
        כל ספק וקבלן נשמר פעם אחת לכל החברה. בפרויקט חדש רק משייכים את הקבלנים הרלוונטיים ואת
        התחומים שלהם. אנשי הקבלן נכנסים לפורטל ורואים רק את הליקויים שלהם.
      </p>
      {notice !== null && (
        <p
          className={`notice notice--${notice.kind}`}
          role={notice.kind === 'error' ? 'alert' : 'status'}
        >
          {notice.text}
        </p>
      )}
      <Trades catalog={catalog} onSave={save} />
      <h3>קבלנים מבצעים ({contractors.length})</h3>
      <ul className="supplier-list">
        {contractors.map((s) => (
          <SupplierItem
            key={s.id}
            supplier={s}
            catalog={catalog}
            onSave={save}
            onNotice={setNotice}
          />
        ))}
      </ul>
      <AddSupplier catalog={catalog} onSave={save} />
      {materials.length > 0 && (
        <>
          <h3>ספקי חומרים ({materials.length})</h3>
          <ul className="supplier-list">
            {materials.map((s) => (
              <li key={s.id} className="supplier-item">
                <b>{s.name}</b>
                <span className="hint">
                  {s.supplyType === null ? '' : SUPPLY_TYPE_LABELS[s.supplyType]} ·{' '}
                  {s.projects.length} פרויקטים
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

type Save = (promise: Promise<CompanyCatalogResponse>, done: string) => Promise<void>;

function Trades({
  catalog,
  onSave,
}: {
  readonly catalog: CompanyCatalogResponse;
  readonly onSave: Save;
}) {
  const [name, setName] = useState('');
  return (
    <details className="supplier-trades">
      <summary>תחומי עבודה ({catalog.trades.filter((t) => t.active).length})</summary>
      <ul className="trade-chips">
        {catalog.trades.map((t) => (
          <li key={t.id} className={t.active ? '' : 'trade-chips__off'}>
            {t.name}
          </li>
        ))}
      </ul>
      <form
        className="inline-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim() === '') return;
          void onSave(
            apiPost(
              '/company-trades',
              { name: name.trim(), active: true },
              companyCatalogResponseSchema,
            ),
            'התחום נוסף.',
          ).then(() => {
            setName('');
          });
        }}
      >
        <label className="field">
          <span>תחום חדש</span>
          <input
            value={name}
            maxLength={80}
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
        </label>
        <button type="submit" className="button">
          <Icon name="plus" /> הוספה
        </button>
      </form>
    </details>
  );
}

function AddSupplier({
  catalog,
  onSave,
}: {
  readonly catalog: CompanyCatalogResponse;
  readonly onSave: Save;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [trades, setTrades] = useState<string[]>([]);
  if (!open)
    return (
      <button
        type="button"
        className="button"
        onClick={() => {
          setOpen(true);
        }}
      >
        <Icon name="plus" /> קבלן חדש
      </button>
    );
  return (
    <form
      className="supplier-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim() === '') return;
        void onSave(
          apiPost(
            '/company-suppliers',
            {
              kind: 'contractor',
              name: name.trim(),
              supplyType: null,
              contactName: null,
              contactPhone: phone.trim() === '' ? null : phone.trim(),
              contactEmail: null,
              tradeIds: trades,
              active: true,
            },
            companyCatalogResponseSchema,
          ),
          'הקבלן נוסף לרשימת החברה.',
        ).then(() => {
          setOpen(false);
          setName('');
          setPhone('');
          setTrades([]);
        });
      }}
    >
      <label className="field">
        <span>שם הקבלן</span>
        <input
          value={name}
          maxLength={80}
          required
          onChange={(e) => {
            setName(e.target.value);
          }}
        />
      </label>
      <label className="field">
        <span>טלפון (רשות)</span>
        <input
          value={phone}
          inputMode="tel"
          maxLength={25}
          onChange={(e) => {
            setPhone(e.target.value);
          }}
        />
      </label>
      <TradePicker trades={catalog.trades} value={trades} onChange={setTrades} />
      <div className="form-actions">
        <button type="submit" className="button button--primary">
          שמירה
        </button>
        <button
          type="button"
          className="button button--ghost"
          onClick={() => {
            setOpen(false);
          }}
        >
          ביטול
        </button>
      </div>
    </form>
  );
}

function TradePicker({
  trades,
  value,
  onChange,
}: {
  readonly trades: CompanyCatalogResponse['trades'];
  readonly value: readonly string[];
  readonly onChange: (next: string[]) => void;
}) {
  return (
    <fieldset className="trade-picker">
      <legend>תחומים</legend>
      {trades
        .filter((t) => t.active)
        .map((t) => (
          <label key={t.id} className="choice">
            <input
              type="checkbox"
              checked={value.includes(t.id)}
              onChange={(e) => {
                onChange(e.target.checked ? [...value, t.id] : value.filter((v) => v !== t.id));
              }}
            />
            <span>{t.name}</span>
          </label>
        ))}
    </fieldset>
  );
}

function SupplierItem({
  supplier,
  catalog,
  onSave,
  onNotice,
}: {
  readonly supplier: CompanySupplier;
  readonly catalog: CompanyCatalogResponse;
  readonly onSave: Save;
  readonly onNotice: (notice: Notice) => void;
}) {
  const id = useId();
  const [project, setProject] = useState('');
  const [trades, setTrades] = useState<string[]>(supplier.tradeIds);
  const [person, setPerson] = useState({ name: '', email: '' });
  const [link, setLink] = useState<string | null>(null);
  const tradeNames = catalog.trades
    .filter((t) => supplier.tradeIds.includes(t.id))
    .map((t) => t.name);
  const unassigned = catalog.projects.filter((p) => !supplier.projects.some((x) => x.id === p.id));
  return (
    <li className="supplier-item">
      <details>
        <summary>
          <b>{supplier.name}</b>
          <span className="hint">
            {tradeNames.length > 0
              ? tradeNames.join(', ')
              : COMPANY_SUPPLIER_KIND_LABELS[supplier.kind]}{' '}
            · {supplier.projects.length} פרויקטים · {supplier.people.length} אנשי קשר
          </span>
        </summary>
        <div className="supplier-item__body">
          <h4 id={`${id}-projects`}>פרויקטים</h4>
          {supplier.projects.length === 0 ? (
            <p className="hint">עדיין לא שויך לפרויקט.</p>
          ) : (
            <ul className="trade-chips" aria-labelledby={`${id}-projects`}>
              {supplier.projects.map((p) => (
                <li key={p.id}>{p.name}</li>
              ))}
            </ul>
          )}
          {unassigned.length > 0 && (
            <form
              className="supplier-form"
              onSubmit={(e) => {
                e.preventDefault();
                if (project === '') return;
                apiPost(
                  `/projects/${project}/company-suppliers`,
                  { supplierId: supplier.id, tradeIds: trades },
                  {
                    parse: (v: unknown) => v,
                  },
                )
                  .then(() =>
                    onSave(
                      apiGet('/company-suppliers', companyCatalogResponseSchema),
                      'הקבלן שויך לפרויקט.',
                    ),
                  )
                  .catch((error: unknown) => {
                    onNotice({ kind: 'error', text: explain(error) });
                  });
              }}
            >
              <label className="field">
                <span>שיוך לפרויקט</span>
                <select
                  value={project}
                  onChange={(e) => {
                    setProject(e.target.value);
                  }}
                >
                  <option value="">בחירת פרויקט</option>
                  {unassigned.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </label>
              <TradePicker trades={catalog.trades} value={trades} onChange={setTrades} />
              <button type="submit" className="button" disabled={project === ''}>
                שיוך
              </button>
            </form>
          )}

          <h4>אנשי הקבלן בפורטל</h4>
          {supplier.people.length === 0 && <p className="hint">עדיין אין אנשי קשר עם גישה.</p>}
          <ul className="supplier-people">
            {supplier.people.map((p) => (
              <li key={p.employeeId}>
                <span>
                  <b>{p.name}</b> <span className="hint">{p.email}</span>
                </span>
                <span className="hint">
                  {p.account === 'active'
                    ? 'חשבון פעיל'
                    : p.account === 'pending'
                      ? 'ממתין לכניסה ראשונה'
                      : 'ללא חשבון'}
                </span>
                <fieldset className="trade-picker">
                  <legend>פרויקטים בפורטל</legend>
                  {supplier.projects.map((proj) => (
                    <label key={proj.id} className="choice">
                      <input
                        type="checkbox"
                        checked={p.projects.includes(proj.id)}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...p.projects, proj.id]
                            : p.projects.filter((x) => x !== proj.id);
                          void onSave(
                            apiPut(
                              `/company-suppliers/${supplier.id}/people/${p.employeeId}/projects`,
                              { projectIds: next },
                              companyCatalogResponseSchema,
                            ),
                            'הגישה לפרויקטים עודכנה.',
                          );
                        }}
                      />
                      <span>{proj.name}</span>
                    </label>
                  ))}
                </fieldset>
                <button
                  type="button"
                  className="button button--small"
                  onClick={() => {
                    setLink(null);
                    apiPost(`/employees/${p.employeeId}/invitation`, {}, invitationResponseSchema)
                      .then((r) => {
                        setLink(r.link);
                        onNotice({
                          kind: 'success',
                          text: 'נוצר קישור הזמנה חד־פעמי. שלחו אותו לאיש הקשר בלבד.',
                        });
                      })
                      .catch((error: unknown) => {
                        onNotice({ kind: 'error', text: explain(error) });
                      });
                  }}
                >
                  קישור הזמנה
                </button>
              </li>
            ))}
          </ul>
          {link !== null && (
            <p className="notice notice--info supplier-link">
              <span>קישור הזמנה (חד־פעמי):</span> <bdi dir="ltr">{link}</bdi>
            </p>
          )}
          <form
            className="supplier-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (person.name.trim() === '' || person.email.trim() === '') return;
              void onSave(
                apiPost(
                  `/company-suppliers/${supplier.id}/people`,
                  person,
                  companyCatalogResponseSchema,
                ),
                'איש הקשר נוסף. סמנו את הפרויקטים שלו וצרו קישור הזמנה.',
              ).then(() => {
                setPerson({ name: '', email: '' });
              });
            }}
          >
            <label className="field">
              <span>איש קשר חדש</span>
              <input
                value={person.name}
                maxLength={120}
                placeholder="שם מלא"
                onChange={(e) => {
                  setPerson((v) => ({ ...v, name: e.target.value }));
                }}
              />
            </label>
            <label className="field">
              <span>דוא״ל</span>
              <input
                value={person.email}
                type="email"
                inputMode="email"
                dir="ltr"
                maxLength={254}
                onChange={(e) => {
                  setPerson((v) => ({ ...v, email: e.target.value }));
                }}
              />
            </label>
            <button type="submit" className="button">
              <Icon name="plus" /> הוספת איש קשר
            </button>
          </form>
        </div>
      </details>
    </li>
  );
}
